/**
 * Session list component — displays user sessions as filterable, sortable cards
 */

import React, { useState } from 'react';
import { SessionResponse } from '../services/sessionService';

// Alias for the expected prop shape (subset of SessionResponse)
export interface SessionSummary {
  id: string;
  name: string;
  version_number?: number;
  completion_status?: string;
  is_active?: boolean;
  created_at: string;
  updated_at: string;
  parent_session_id?: string | null;
}

interface SessionListProps {
  sessions: SessionSummary[];
  onOpen: (id: string) => void;
  onResume: (id: string) => void;
  onDelete: (id: string) => void;
  onRename?: (id: string, newName: string) => void;
  isLoading?: boolean;
}

type StatusFilter = 'all' | 'in-progress' | 'completed';
type SortOption = 'date-desc' | 'date-asc' | 'name-asc' | 'name-desc';

const statusLabel = (session: SessionSummary): string => {
  if (session.completion_status) {
    return session.completion_status;
  }
  if (session.is_active === false) {
    return 'Completed';
  }
  return 'In Progress';
};

const isCompleted = (session: SessionSummary): boolean => {
  const label = statusLabel(session).toLowerCase();
  return label === 'completed' || label === 'complete';
};

const formatDate = (iso: string): string => {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
};

const badgeStyle = (completed: boolean): React.CSSProperties => ({
  display: 'inline-block',
  padding: '2px 10px',
  borderRadius: '12px',
  fontSize: '0.78rem',
  fontWeight: 'bold',
  backgroundColor: completed ? '#d4edda' : '#fff3cd',
  color: completed ? '#155724' : '#856404',
  border: `1px solid ${completed ? '#c3e6cb' : '#ffc107'}`,
});

const SessionList: React.FC<SessionListProps> = ({
  sessions,
  onOpen,
  onResume,
  onDelete,
  onRename,
  isLoading = false,
}) => {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortOption>('date-desc');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');

  // Filter
  const filtered = sessions.filter((s) => {
    const matchesSearch = s.name.toLowerCase().includes(search.toLowerCase());

    let matchesStatus = true;
    if (statusFilter === 'in-progress') matchesStatus = !isCompleted(s);
    if (statusFilter === 'completed') matchesStatus = isCompleted(s);

    return matchesSearch && matchesStatus;
  });

  // Sort
  const sorted = [...filtered].sort((a, b) => {
    switch (sort) {
      case 'date-desc':
        return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
      case 'date-asc':
        return new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime();
      case 'name-asc':
        return a.name.localeCompare(b.name);
      case 'name-desc':
        return b.name.localeCompare(a.name);
      default:
        return 0;
    }
  });

  const containerStyle: React.CSSProperties = {
    padding: '0',
  };

  const controlsRowStyle: React.CSSProperties = {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
    marginBottom: '24px',
    alignItems: 'center',
  };

  const inputStyle: React.CSSProperties = {
    padding: '8px 12px',
    fontSize: '0.95rem',
    border: '2px solid #ccc',
    borderRadius: '4px',
    outline: 'none',
    flex: '1 1 180px',
    minWidth: '140px',
  };

  const selectStyle: React.CSSProperties = {
    padding: '8px 12px',
    fontSize: '0.95rem',
    border: '2px solid #ccc',
    borderRadius: '4px',
    outline: 'none',
    backgroundColor: '#fff',
    cursor: 'pointer',
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '20px 24px',
    marginBottom: '16px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
  };

  const cardHeaderStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '8px',
    gap: '12px',
    flexWrap: 'wrap',
  };

  const namStyle: React.CSSProperties = {
    fontSize: '1.1rem',
    fontWeight: 'bold',
    color: '#222',
    flex: 1,
    minWidth: 0,
    wordBreak: 'break-word',
  };

  const metaStyle: React.CSSProperties = {
    fontSize: '0.85rem',
    color: '#888',
    marginBottom: '14px',
  };

  const actionsStyle: React.CSSProperties = {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  };

  const btnBase: React.CSSProperties = {
    padding: '7px 16px',
    fontSize: '0.9rem',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 'bold',
    transition: 'background-color 0.2s',
  };

  const emptyStyle: React.CSSProperties = {
    textAlign: 'center',
    color: '#888',
    padding: '48px 0',
    fontSize: '1rem',
  };

  const loadingStyle: React.CSSProperties = {
    textAlign: 'center',
    color: '#888',
    padding: '48px 0',
  };

  if (isLoading) {
    return (
      <div style={containerStyle}>
        <div style={loadingStyle}>Loading sessions...</div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {/* Controls */}
      <div style={controlsRowStyle}>
        <input
          type="text"
          placeholder="Search by name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={inputStyle}
          aria-label="Search sessions"
        />

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          style={selectStyle}
          aria-label="Filter by status"
        >
          <option value="all">All Sessions</option>
          <option value="in-progress">In Progress</option>
          <option value="completed">Completed</option>
        </select>

        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as SortOption)}
          style={selectStyle}
          aria-label="Sort sessions"
        >
          <option value="date-desc">Newest First</option>
          <option value="date-asc">Oldest First</option>
          <option value="name-asc">Name (A-Z)</option>
          <option value="name-desc">Name (Z-A)</option>
        </select>
      </div>

      {/* Session Cards */}
      {sorted.length === 0 ? (
        <div style={emptyStyle}>
          {search || statusFilter !== 'all'
            ? 'No sessions match your filters.'
            : 'No sessions yet. Start a new analysis to get started.'}
        </div>
      ) : (
        sorted.map((session) => {
          const completed = isCompleted(session);
          const label = statusLabel(session);

          return (
            <div key={session.id} style={cardStyle}>
              <div style={cardHeaderStyle}>
                {editingId === session.id ? (
                  <input
                    autoFocus
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const trimmed = editValue.trim();
                        if (trimmed && trimmed !== session.name && onRename) {
                          onRename(session.id, trimmed);
                        }
                        setEditingId(null);
                      } else if (e.key === 'Escape') {
                        setEditingId(null);
                      }
                    }}
                    onBlur={() => {
                      const trimmed = editValue.trim();
                      if (trimmed && trimmed !== session.name && onRename) {
                        onRename(session.id, trimmed);
                      }
                      setEditingId(null);
                    }}
                    style={{
                      ...namStyle,
                      border: '2px solid #007bff',
                      borderRadius: '4px',
                      padding: '2px 6px',
                      outline: 'none',
                      backgroundColor: '#fff',
                    }}
                    aria-label="Rename session"
                  />
                ) : (
                  <span
                    style={{ ...namStyle, cursor: onRename ? 'pointer' : 'default' }}
                    onClick={() => {
                      if (onRename) {
                        setEditingId(session.id);
                        setEditValue(session.name);
                      }
                    }}
                    title={onRename ? 'Click to rename' : undefined}
                  >
                    {session.name}
                    {onRename && (
                      <span style={{ marginLeft: '6px', fontSize: '0.85rem', color: '#999' }}>
                        ✎
                      </span>
                    )}
                  </span>
                )}
                <span style={badgeStyle(completed)}>{label}</span>
              </div>

              <div style={metaStyle}>
                Created: {formatDate(session.created_at)} &nbsp;&bull;&nbsp; Updated:{' '}
                {formatDate(session.updated_at)}
                {session.version_number !== undefined && session.version_number > 1 && (
                  <> &nbsp;&bull;&nbsp; Version {session.version_number}</>
                )}
              </div>

              <div style={actionsStyle}>
                <button
                  onClick={() => onOpen(session.id)}
                  style={{ ...btnBase, backgroundColor: '#007bff', color: '#fff' }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#0056b3')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#007bff')}
                >
                  View
                </button>

                {!completed && (
                  <button
                    onClick={() => onResume(session.id)}
                    style={{ ...btnBase, backgroundColor: '#28a745', color: '#fff' }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#1e7e34')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#28a745')}
                  >
                    Resume
                  </button>
                )}

                <button
                  onClick={() => onDelete(session.id)}
                  style={{ ...btnBase, backgroundColor: '#dc3545', color: '#fff' }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#a71d2a')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#dc3545')}
                >
                  Delete
                </button>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

export default SessionList;
